/**
 * Lift2Fit — the human coach connection, lifter side.
 *
 * One relationship, one record: a lifter may hold at most one human coach/gym
 * at a time. The lifter invites by name (email optional — a coach with no
 * account yet can still be invited) and gets a single-use code to send. The
 * lifter owns that link: they choose the shared fields and can revoke it
 * instantly.
 *
 * Everything here reads and writes the live `/coaching` contract only. A real
 * account sees the API's own `detail` on every failure — a stale token or a
 * refused second coach is never dressed up as demo data.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  api,
  type CoachInvite,
  type CoachLink,
  type CrmBooking,
  type CrmSlot,
  type SharedField,
} from '@/lib/api';
import { cn, formatDate, timeAgo } from '@/lib/format';
import { useSession } from '@/lib/useSession';
import { Button } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, CheckIcon, CloseIcon, ShieldIcon, SyncIcon } from '@/lib/icons';
import { LiveMessage, Panel, StatusChip } from './components';
import { Link, navigate } from './router';

/* ---------------------------------------------------------------------------
   Shared vocabulary
   ------------------------------------------------------------------------ */

export const SHARED_FIELD_ORDER: ReadonlyArray<SharedField> = [
  'sessions',
  'sets',
  'bodyweight',
  'pain',
  'notes',
];

const FIELD_LABELS: Record<SharedField, { label: string; hint: string }> = {
  sessions: { label: 'Sessions', hint: 'When you trained and what the session was' },
  sets: { label: 'Sets & load', hint: 'Weight, reps, RPE and volume' },
  bodyweight: { label: 'Bodyweight', hint: 'The bodyweight you log' },
  pain: { label: 'Pain', hint: 'Pain scores recorded against your sets' },
  notes: { label: 'Notes', hint: 'Session and exercise notes you write' },
};

function fieldList(fields: SharedField[]): string {
  const ordered = SHARED_FIELD_ORDER.filter((field) => fields.includes(field));
  return ordered.map((field) => FIELD_LABELS[field].label).join(', ');
}

/**
 * The one-line pack summary an invite carries (migration 0020). The discovery
 * page and the invite read the same way: name · kind · price/cadence · places.
 */
function packSummary(pack: CoachInvite['pack']): string | null {
  if (!pack) return null;
  const kind =
    pack.kind === 'in_person' ? 'In person' : pack.kind === 'semi_coaching' ? 'Semi-coaching' : 'Online';
  const cadence = pack.cadence === 'one_off' ? '' : pack.cadence === 'weekly' ? ' / week' : ' / month';
  const places = `${pack.capacity} ${pack.capacity === 1 ? 'person' : 'people'}`;
  const coaches = pack.coachCount > 1 ? `, ${pack.coachCount} coaches` : '';
  return `${pack.name} · ${kind} · ${pack.currency} ${pack.price.toFixed(2)}${cadence} · ${places}${coaches}`;
}

/** The absolute share URL a coach can open, built from the API's relative path. */
export function inviteShareLink(invite: CoachInvite): string {
  const path = invite.shareUrl || `/coach/join?code=${encodeURIComponent(invite.code)}`;
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* clipboard blocked — the value is still visible and selectable */
  }
  return false;
}

/* ---------------------------------------------------------------------------
   State
   ------------------------------------------------------------------------ */

export interface CoachingState {
  loading: boolean;
  /** True once a first answer (even an empty one) has been rendered. */
  loaded: boolean;
  error: string | null;
  link: CoachLink | null;
  invites: CoachInvite[];
  reload: () => void;
}

/** One loader for the relationship + invites, shared by profile and dashboard. */
export function useCoaching(): CoachingState {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<CoachLink | null>(null);
  const [invites, setInvites] = useState<CoachInvite[]>([]);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([api.coachingLink(), api.coachingInvites()])
      .then(([linkResult, invitesResult]) => {
        if (!alive) return;
        const problem = linkResult.problem ?? invitesResult.problem;
        if (problem) {
          setError(problem.detail ?? problem.title);
        } else {
          setLink(linkResult.data.link);
          setInvites(invitesResult.data);
        }
        setLoading(false);
        setLoaded(true);
      })
      .catch((cause: unknown) => {
        if (!alive) return;
        setError(cause instanceof Error ? cause.message : 'Could not read your coach connection.');
        setLoading(false);
        setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  return { loading, loaded, error, link, invites, reload };
}

/* ---------------------------------------------------------------------------
   Shared fields — the lifter's control
   ------------------------------------------------------------------------ */

export function SharedFieldsControl({
  sharedFields,
  onSaved,
}: {
  sharedFields: SharedField[];
  onSaved: (link: CoachLink) => void;
}) {
  const [selected, setSelected] = useState<SharedField[]>(sharedFields);
  const [busy, setBusy] = useState<SharedField | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');
  const signature = [...sharedFields].sort().join(',');

  useEffect(() => {
    setSelected(sharedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const toggle = async (field: SharedField) => {
    const next = selected.includes(field)
      ? selected.filter((value) => value !== field)
      : SHARED_FIELD_ORDER.filter((value) => value === field || selected.includes(value));
    if (next.length === 0) {
      setTone('danger');
      setMessage('Keep at least one field shared. To hide everything, revoke the link below instead.');
      return;
    }
    setBusy(field);
    setTone('info');
    setMessage('Saving…');
    const result = await api.updateCoachSharedFields(next);
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That change did not save.');
      return;
    }
    setSelected(result.data.sharedFields);
    onSaved(result.data);
    setTone('success');
    setMessage(
      next.includes(field)
        ? `${FIELD_LABELS[field].label} shared — your coach can see it from now on.`
        : `${FIELD_LABELS[field].label} hidden — your coach can no longer see it.`,
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Fields shared with your coach">
        {SHARED_FIELD_ORDER.map((field) => {
          const active = selected.includes(field);
          return (
            <button
              key={field}
              type="button"
              data-testid={`shared-field-${field}`}
              aria-pressed={active}
              disabled={busy !== null}
              onClick={() => void toggle(field)}
              className={cn(
                'flex min-w-[150px] flex-1 flex-col items-start gap-0.5 border px-3 py-2 text-left transition-colors duration-150 disabled:opacity-60',
                active ? 'border-accent bg-page-subtle' : 'border-hairline bg-surface hover:border-accent',
              )}
            >
              <span className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.06em] text-ink">
                {active ? <CheckIcon className="text-success" /> : <CloseIcon className="text-ink-muted" />}
                {FIELD_LABELS[field].label}
                <span className="sr-only">{active ? 'shared' : 'not shared'}</span>
              </span>
              <span className="text-[11px] text-ink-muted">{FIELD_LABELS[field].hint}</span>
            </button>
          );
        })}
      </div>
      <p className="text-[12px] text-ink-muted">
        An unshared field is invisible to your coach — it is never sent, not merely hidden in their view.
      </p>
      <LiveMessage message={message} tone={tone} />
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Connect my coach — the invite form and its shareable code
   ------------------------------------------------------------------------ */

export function ConnectCoachForm({
  onInvited,
  triggerLabel = 'Connect my coach',
}: {
  onInvited: () => void;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<CoachInvite | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (name.trim().length === 0) {
      setError('Give your coach a name — the code is addressed to them.');
      return;
    }
    setBusy(true);
    setError(null);
    const result = await api.inviteCoach({
      name: name.trim(),
      ...(email.trim() ? { email: email.trim() } : {}),
      sharedFields: ['sessions', 'sets'],
    });
    setBusy(false);
    if (result.problem || !result.data) {
      setError(result.problem?.detail ?? 'The invite could not be created.');
      return;
    }
    setInvite(result.data);
    setCopied(false);
    onInvited();
  };

  if (!open && !invite) {
    return (
      <div className="flex flex-col gap-2">
        <Button variant="secondary" data-testid="connect-coach" onClick={() => setOpen(true)}>
          {triggerLabel}
        </Button>
        <p className="text-[12px] text-ink-muted">
          One coach or gym at a time. You choose what they see and can revoke it instantly.
        </p>
      </div>
    );
  }

  if (invite) {
    const shareLink = inviteShareLink(invite);
    return (
      <div className="flex flex-col gap-3 border border-accent bg-page-subtle p-4" data-testid="coach-invite-result">
        <p className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink">
          Send this to {invite.inviteeName ?? invite.inviteeEmail ?? 'your coach'} — they can create an account with it
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <code
            data-testid="coach-invite-code"
            className="l2f-tabular border border-hairline bg-surface px-3 py-2 font-mono text-[15px] font-bold tracking-[0.04em] text-ink"
          >
            {invite.code}
          </code>
          <Button
            variant="outline"
            data-testid="coach-invite-copy"
            onClick={() => {
              void copyText(shareLink).then((ok) => {
                setCopied(ok);
              });
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>
        <input
          readOnly
          aria-label="Invite link"
          data-testid="coach-invite-link"
          value={shareLink}
          className="h-11 w-full border border-hairline bg-surface px-3 text-[13px] text-ink-muted"
        />
        <p className="text-[12px] text-ink-muted">
          <span className="font-bold text-ink">Single use.</span> It works once, for one coach, and expires on{' '}
          {formatDate(invite.expiresAt)}. If they already have an account they sign in with it instead of creating a
          new profile.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setInvite(null);
              setOpen(false);
              setName('');
              setEmail('');
            }}
          >
            Done
          </Button>
        </div>
        <LiveMessage message={null} />
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} data-testid="connect-coach-form">
      <label className="flex flex-col gap-1.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Coach name</span>
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Sam Taylor"
          data-testid="coach-name-input"
          className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
          Coach email (optional)
        </span>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="coach@example.com"
          data-testid="coach-email-input"
          className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
        />
      </label>
      <p className="text-[12px] text-ink-muted">
        No email? Leave it blank and send the code instead — a coach with no account can create a lightweight profile
        from it.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="secondary" disabled={busy} data-testid="coach-invite-submit">
          {busy ? 'Creating…' : 'Send invite'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
      </div>
      <LiveMessage message={error} tone="danger" />
    </form>
  );
}

/* ---------------------------------------------------------------------------
   Pending invites — sent (revoke) and received (accept / decline)
   ------------------------------------------------------------------------ */

export function PendingInvites({
  invites,
  onChanged,
  onNotice,
}: {
  invites: CoachInvite[];
  onChanged: () => void;
  onNotice?: (message: string, tone: 'info' | 'success' | 'danger') => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');

  const say = (next: string, nextTone: 'info' | 'success' | 'danger') => {
    setMessage(next);
    setTone(nextTone);
    onNotice?.(next, nextTone);
  };

  const sent = invites.filter((invite) => invite.direction === 'sent' && invite.status === 'pending');
  const received = invites.filter((invite) => invite.direction === 'received' && invite.status === 'pending');
  // Declines the lifter has not acted on any more — and, because a decline must
  // never be silent, the note the coach left. An ACCEPTED request is deliberately
  // not listed: its message is already the first chat message, and keeping the
  // old coach's name on this page after a revoke would be misleading.
  const answered = invites
    .filter((invite) => invite.decisionMessage !== null && invite.status !== 'accepted')
    .slice(0, 5);
  if (sent.length === 0 && received.length === 0 && answered.length === 0) return null;

  const cancel = async (invite: CoachInvite) => {
    setBusy(invite.id);
    say('Cancelling…', 'info');
    const result = await api.cancelCoachInvite(invite.code);
    setBusy(null);
    if (result.problem || !result.data) {
      say(result.problem?.detail ?? 'The invite could not be cancelled.', 'danger');
      return;
    }
    say('Invite cancelled. That code no longer works.', 'success');
    onChanged();
  };

  const accept = async (invite: CoachInvite) => {
    setBusy(invite.id);
    say('Accepting…', 'info');
    const result = await api.acceptCoachInvite(invite.code);
    setBusy(null);
    if (result.problem || !result.data) {
      // The API's own words: "An account with that email already exists; sign
      // in to accept this invite." Never flatten that into a generic failure —
      // and say exactly what to do about it.
      const detail = result.problem?.detail ?? 'That invite could not be accepted.';
      const mustSignIn = result.problem?.status === 409 && /sign in to accept/i.test(detail);
      say(
        mustSignIn
          ? `${detail} Sign in with ${invite.inviteeEmail ?? 'the invited email'} and accept it from your Manage coach page.`
          : detail,
        'danger',
      );
      return;
    }
    say('Invite accepted — the coach link is live.', 'success');
    onChanged();
  };

  const decline = async (invite: CoachInvite) => {
    setBusy(invite.id);
    say('Declining…', 'info');
    const result = await api.declineCoachInvite(invite.code);
    setBusy(null);
    if (result.problem || !result.data) {
      say(result.problem?.detail ?? 'That invite could not be declined.', 'danger');
      return;
    }
    say('Invite declined.', 'success');
    onChanged();
  };

  return (
    <div className="flex flex-col gap-3" data-testid="coach-pending-invites">
      <h3 className="text-[12px] font-bold uppercase tracking-[0.1em] text-ink-muted">Pending invites</h3>
      {received.map((invite) => (
        <div key={invite.id} className="flex flex-col gap-2 border-l-2 border-l-accent bg-page-subtle px-3 py-2">
          <p className="text-[14px] font-bold text-ink">
            {invite.coach?.displayName ?? invite.inviter?.displayName ?? 'A coach'} invited you
          </p>
          <p className="text-[12px] text-ink-muted">
            They would see {fieldList(invite.sharedFields) || 'nothing yet'}. Expires {formatDate(invite.expiresAt)}.
          </p>
          {invite.pack ? (
            <p className="text-[12px] font-bold text-ink" data-testid={`invite-pack-${invite.id}`}>
              Offers “{packSummary(invite.pack)}”
            </p>
          ) : null}
          {invite.inviteeEmail ? (
            <p className="text-[12px] text-ink-muted">Sent to {invite.inviteeEmail}.</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy !== null}
              data-testid={`invite-accept-${invite.id}`}
              onClick={() => void accept(invite)}
            >
              Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              data-testid={`invite-decline-${invite.id}`}
              onClick={() => void decline(invite)}
            >
              Decline
            </Button>
          </div>
        </div>
      ))}
      {sent.map((invite) => (
        <div key={invite.id} className="flex flex-col gap-2 border border-hairline px-3 py-2">
          <p className="flex items-center gap-2 text-[14px] font-bold text-ink">
            <SyncIcon className="text-accent" />
            Invite sent to {invite.inviteeName ?? invite.inviteeEmail ?? 'a share link'}
          </p>
          <p className="text-[12px] text-ink-muted">
            Shares {fieldList(invite.sharedFields) || 'nothing'} · expires {formatDate(invite.expiresAt)}. Until they
            accept, they can see nothing.
          </p>
          {invite.pack ? (
            <p className="text-[12px] font-bold text-ink" data-testid={`invite-pack-${invite.id}`}>
              Offers “{packSummary(invite.pack)}”
            </p>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            data-testid={`invite-cancel-${invite.id}`}
            onClick={() => void cancel(invite)}
          >
            Revoke invite
          </Button>
        </div>
      ))}
      {answered.length > 0 ? (
        <div className="flex flex-col gap-2" data-testid="coach-invite-answers">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Answers</span>
          {answered.map((invite) => {
            const accepted = invite.status === 'accepted';
            const fromCoach = invite.coach?.displayName ?? 'Your coach';
            return (
              <div
                key={invite.id}
                data-testid={`coach-invite-answer-${invite.id}`}
                className="flex flex-col gap-1 border border-hairline px-3 py-2"
              >
                <p className="flex flex-wrap items-center gap-2 text-[13px] font-bold text-ink">
                  {accepted ? 'Connected' : 'Declined'}
                  <span className="text-[12px] font-normal text-ink-muted">
                    {invite.direction === 'sent' ? `${fromCoach} answered your request` : 'Your answer'}
                  </span>
                </p>
                {invite.decisionMessage ? (
                  <p className="text-[13px] text-ink" data-testid={`coach-invite-message-${invite.id}`}>
                    “{invite.decisionMessage}”
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      <LiveMessage message={message} tone={tone} />
    </div>
  );
}
/* ---------------------------------------------------------------------------
   The compact dashboard card
   ------------------------------------------------------------------------ */

export function YourCoachCard() {
  const state = useCoaching();
  const link = state.link;
  const active = link?.status === 'active' && link.coach !== null;

  return (
    <Panel
      title="Your human coach"
      subtitle="A real person, at most one at a time, sharing only what you tick."
      actions={
        <Link
          to="/my-coach"
          className="inline-flex h-9 items-center gap-1.5 border border-hairline px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
        >
          Manage
          <ArrowRightIcon />
        </Link>
      }
    >
      {state.loading && !state.loaded ? (
        <p className="text-[13px] text-ink-muted">Loading your coach connection…</p>
      ) : state.error ? (
        <LiveMessage message={state.error} tone="danger" />
      ) : active && link ? (
        <div className="flex flex-col gap-1.5">
          <p className="flex items-center gap-2 text-[15px] font-bold text-ink">
            <ShieldIcon className="text-accent" />
            {link.coach?.displayName ?? 'Your coach'}
          </p>
          <p className="text-[13px] text-ink-muted">
            Linked {link.linkedAt ? formatDate(link.linkedAt) : 'recently'} · shares{' '}
            {fieldList(link.sharedFields) || 'nothing'}.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-[13px] text-ink-muted">
            No human coach linked. Manage your coach from your own page — you choose what they see and can revoke it
            instantly.
          </p>
          <Link
            to="/my-coach"
            data-testid="dashboard-connect-coach"
            className="inline-flex w-fit h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            Connect my coach
            <ArrowRightIcon />
          </Link>
        </div>
      )}
    </Panel>
  );
}

/* ---------------------------------------------------------------------------
   The join screen behind the share link (`/coach/join?code=…`)
   ------------------------------------------------------------------------ */

function readJoinCode(): string {
  if (typeof window === 'undefined') return '';
  const fromSearch = new URLSearchParams(window.location.search).get('code');
  if (fromSearch) return fromSearch;
  const hash = window.location.hash;
  const queryAt = hash.indexOf('?');
  if (queryAt >= 0) return new URLSearchParams(hash.slice(queryAt)).get('code') ?? '';
  return '';
}

export function CoachJoinScreen() {
  const session = useSession();
  const code = readJoinCode();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [preview, setPreview] = useState<CoachInvite | null>(null);

  // Read the invite (and any pack it offers) before accepting. Public: the
  // invitee may have no account yet.
  useEffect(() => {
    if (!code) return;
    let alive = true;
    void api.invitePreview(code).then((result) => {
      if (alive && result.data) setPreview(result.data);
    });
    return () => {
      alive = false;
    };
  }, [code]);

  const accept = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const signedIn = Boolean(session.user);
    const profile =
      !signedIn && email.trim() && displayName.trim() && password.length >= 8
        ? { email: email.trim(), displayName: displayName.trim(), password }
        : undefined;
    const result = await api.acceptCoachInvite(code, profile);
    if (result.problem || !result.data) {
      setBusy(false);
      const detail = result.problem?.detail ?? 'That invite could not be accepted.';
      const mustSignIn = result.problem?.status === 409 && /sign in to accept/i.test(detail);
      setError(mustSignIn ? `${detail} Use “I already have an account” above to sign in.` : detail);
      return;
    }
    if (!signedIn && profile) {
      // The code created a lightweight profile; sign in so this device adopts
      // the session the API just issued.
      const login = await session.login(profile.email, profile.password);
      setBusy(false);
      if (!login.ok) {
        setError(login.error ?? 'Your profile was created — sign in to continue.');
        return;
      }
    } else {
      setBusy(false);
    }
    setDone(true);
    navigate('/my-coach');
  };

  if (!code) {
    return (
      <Shell title="That invite link is incomplete">
        <p className="text-[15px] text-ink-muted">
          The link is missing its code. Ask the lifter or coach who invited you to send the full link again.
        </p>
        <Link to="/login" className="inline-flex h-11 items-center bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent">
          Sign in
        </Link>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell title="You are linked">
        <p className="text-[15px] text-ink-muted">The coach connection is live. Opening your Manage coach page…</p>
        <Link to="/my-coach" className="inline-flex h-11 items-center bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent">
          Go to Manage coach
        </Link>
      </Shell>
    );
  }

  return (
    <Shell title="Accept your coach invite" testId="coach-join">
      <p className="text-[15px] text-ink-muted">
        One code, one relationship. {session.user ? `Signed in as ${session.user.email}.` : 'Create a lightweight profile to accept, or sign in if you already have an account.'}
      </p>
      {preview?.pack ? (
        <p
          className="border-l-2 border-accent bg-page-subtle px-3 py-2 text-[13px] font-bold text-ink"
          data-testid="coach-join-pack"
        >
          They are offering “{packSummary(preview.pack)}”
        </p>
      ) : null}
      <form className="flex flex-col gap-3" onSubmit={accept}>
        {!session.user ? (
          <>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Your name</span>
              <input
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                data-testid="coach-join-name"
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                data-testid="coach-join-email"
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                Password (8+ characters)
              </span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                data-testid="coach-join-password"
                className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
              />
            </label>
          </>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={busy} data-testid="coach-join-accept">
            {busy ? 'Accepting…' : 'Accept invite'}
          </Button>
          <Link to="/login" className="inline-flex h-11 items-center border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent">
            I already have an account
          </Link>
        </div>
        <LiveMessage message={error} tone="danger" />
      </form>
    </Shell>
  );
}

function Shell({ title, children, testId }: { title: string; children: ReactNode; testId?: string }) {
  return (
    <div className="l2f-app flex min-h-screen items-center justify-center bg-page px-4 py-10" data-testid={testId}>
      <div className="flex w-full max-w-[520px] flex-col gap-4 border border-hairline bg-surface p-6">
        <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.1em] text-accent">
          <AlertIcon />
          Coach invite
        </span>
        <h1 className="font-display text-2xl font-black uppercase text-ink">{title}</h1>
        {children}
      </div>
    </div>
  );
}
